import { MenuItem } from '@mui/material';
import update from 'immutability-helper';
import { useTranslation } from 'next-i18next';
import React from 'react';
import toast from 'react-hot-toast';
import { MuxAsset } from '../../../../../interfaces/interfaces';
import { VideoComponentInterface } from '../../../../../interfaces/pagebuilder';
import { publishFileToMux } from '../../../../../services/services';
import VySelect from '../../../../common/VySelect';
import VyTextField from '../../../../common/VyTextField';
import VyUpload from '../../../../common/VyUpload';
import { PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { DragCard } from '../../../../common/DragCard';

interface VideoSettingsTableInterface {
  component: VideoComponentInterface;
  onChange: (c: VideoComponentInterface) => void;
}
const MAX_VIDEO_SIZE = 400;

const VideoSettingsTable = ({ component, onChange }: VideoSettingsTableInterface) => {
  const { t } = useTranslation(['pagebuilder']);

  const moveCard = (result: DropResult) => {
    const dragFile = component?.videoSettings?.videoFiles?.[result.source.index];
    if (!dragFile) return;
    const _component = {
      ...component,
      videoSettings: {
        ...(component.videoSettings || []),
        videoFiles: update(component.videoSettings?.videoFiles, {
          $splice: [
            [result.source.index, 1],
            [result.destination?.index!, 0, dragFile],
          ],
        }),
      },
    };
    onChange(_component);
  };

  async function onFileUpload(fileid: string | null, filetype?: string, muxAsset?: MuxAsset) {
    try {
      const _muxAsset = muxAsset ? muxAsset : fileid ? await publishFileToMux(fileid) : null;
      if (!_muxAsset) return;
      const _component: any = {
        ...component,
        videoSettings: {
          ...component.videoSettings,
          videoFile: fileid,
          videoFiles: [...(component.videoSettings?.videoFiles || []), { file: fileid, filetype, ...(_muxAsset ?? {}) }],
        },
      };
      toast(t('toasts:vystem-video-render'));
      onChange(_component);
    } catch (e) {
      console.error(e);
    }
  }

  const settingSections: PageBuilderSettingsSection[] = [
    {
      name: t('content'),
      settings: [
        {
          label: t('pagebuilder:video-component-type-select'),
          content: (
            <VySelect
              variant="outlined"
              onChange={(e) => {
                onChange({ ...component, videoSettings: { ...component.videoSettings, videotype: e.target.value as string } });
              }}
              value={component.videoSettings?.videotype}
              fullWidth>
              <MenuItem value="upload">{t('pagebuilder:video-source-upload')}</MenuItem>
              <MenuItem value="url">{t('pagebuilder:video-source-url')}</MenuItem>
            </VySelect>
          ),
        },
        {
          visible: component?.videoSettings?.videotype === 'url',
          label: t('pagebuilder:video-source-url-label'),
          content: (
            <VyTextField
              value={component.videoSettings?.url}
              onBlur={(e) => {
                onChange({ ...component, videoSettings: { ...component.videoSettings, url: e.target.value as string } });
              }}
            />
          ),
        },
        {
          visible: component?.videoSettings?.videotype === 'upload',
          label: t('pagebuilder:video-upload', { max: MAX_VIDEO_SIZE }),
          content: (
            <VyUpload
              randomExternalImageButton={false}
              acceptedFileTypes="video/*"
              maxFileMb={MAX_VIDEO_SIZE}
              showFileUploadProgress
              getFileObject
              showPreview={false}
              onChange={onFileUpload}
              allowMuxVideos
            />
          ),
        },
        {
          visible: !!component.videoSettings?.videoFiles,
          content: (
            <DragDropContext onDragEnd={moveCard}>
              <Droppable droppableId="videoFiles">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} style={{ minHeight: 50 }} className="flex flex-col gap-2">
                    {component.videoSettings?.videoFiles?.map((el, i) => (
                      <Draggable key={el.file} draggableId={el.file} index={i}>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <DragCard
                              key={el.file}
                              index={i}
                              id={el.file}
                              img={el.file}
                              playback_ids={el.playback_ids}
                              isVideo
                              onDelete={() => {
                                const _files = [...(component.videoSettings?.videoFiles || [])];
                                const i = _files.indexOf(el);
                                _files.splice(i, 1);
                                onChange({ ...component, videoSettings: { ...component.videoSettings, videoFiles: _files } });
                              }}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ),
        },

        {
          label: t('pagebuilder:video-thumbnail'),
          content: (
            <VyUpload
              randomExternalImageButton={false}
              acceptedFileTypes="image/*"
              showPreview={true}
              withCrop={true}
              cropAspectRatio={16 / 9}
              showCropButton={false}
              defaultFileId={component.videoSettings?.thumbnail}
              onChange={(fileid) => {
                const _component: any = { ...component, videoSettings: { ...component.videoSettings, thumbnail: fileid } };
                onChange(_component);
              }}
            />
          ),
        },
      ],
    },
    {
      name: t('player-settings'),
      settings: [
        {
          label: t('account:stage-livestream-autoplay'),
          type: 'boolean',
          path: 'videoSettings.autoplay',
          tooltip: t('account:stage-livestream-autoplay-warning'),
        },
        {
          label: t('pagebuilder:video-muted'),
          type: 'boolean',
          path: 'videoSettings.muted',
          tooltip: t('pagebuilder:video-muted-info'),
        },
      ],
    },
  ];

  return <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} />;
};
export default VideoSettingsTable;
